import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ToastContext, type ToastTone } from './context';
import styles from '../components/ui.module.css';

interface ToastState {
  id: number;
  message: string;
  tone: ToastTone;
}

const VISIBLE_MS = 3600;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextId = useRef(0);

  const show = useCallback((message: string, tone: ToastTone = 'default') => {
    if (timer.current) clearTimeout(timer.current);
    nextId.current += 1;
    setToast({ id: nextId.current, message, tone });
    timer.current = setTimeout(() => setToast(null), VISIBLE_MS);
  }, []);

  useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext value={value}>
      {children}
      <div className={styles.toastRegion} role="status" aria-live="polite">
        {toast && (
          <div
            key={toast.id}
            className={`${styles.toast} ${toast.tone === 'danger' ? styles.toastDanger : ''}`}
          >
            {toast.message}
          </div>
        )}
      </div>
    </ToastContext>
  );
}
